import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Users, ArrowsClockwise, Phone, ChatCircle, EnvelopeSimple, X, Sparkle, ChartLine, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const RAISON_LABEL = {
  uniforme: "🥋 Uniformes en retard",
  prix: "💰 Augmentation de prix",
  horaire: "📅 Changements d'horaire",
  gestion: "📋 Erreurs de gestion",
  discipline: "💢 Discipline / intensité",
  déménagement: "📦 Déménagement",
  santé: "🏥 Santé / blessure",
  famille: "👨‍👩‍👧 Horaires famille",
  possible_double_facturation: "⚠️ Double facturation",
  hausse_tarif_brusque: "📈 Hausse tarif brusque",
  inconnu: "❓ Raison inconnue",
};

const SEGMENT_BADGE = {
  VIP: { label: "VIP", color: "bg-amber-500 text-zinc-950" },
  fidele: { label: "Fidèle", color: "bg-emerald-500 text-zinc-950" },
  essai_long: { label: "Essai long", color: "bg-sky-500 text-zinc-950" },
  essai_court: { label: "Essai court", color: "bg-zinc-500 text-zinc-50" },
  trop_ancien: { label: "Trop ancien", color: "bg-zinc-700 text-zinc-300" },
};

const STATUS_BADGE = {
  a_contacter: { label: "À appeler", color: "bg-red-500 text-white" },
  contacte: { label: "Contacté", color: "bg-amber-500 text-zinc-950" },
  revenu: { label: "Revenu 🎉", color: "bg-emerald-500 text-zinc-950" },
  refus: { label: "Refus", color: "bg-zinc-600 text-zinc-200" },
  a_relancer: { label: "À relancer", color: "bg-sky-500 text-zinc-950" },
};

export default function MemberReconciliationModule() {
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [filter, setFilter] = useState({ status: "a_contacter" });
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [open, setOpen] = useState(null);

  const loadStats = useCallback(async () => {
    try { const r = await api.get("/member-reconciliation/stats"); setStats(r.data); }
    catch {}
  }, []);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set("status", filter.status);
      const r = await api.get(`/member-reconciliation/candidates?${params}`);
      setCandidates(r.data.items || []);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  async function runAnalyze() {
    setAnalyzing(true);
    try {
      const r = await api.post("/member-reconciliation/analyze/now");
      toast.success(`✅ Analyse terminée — ${r.data.candidates} personnes à recontacter`);
      await loadStats(); await loadCandidates();
    } catch (e) {
      toast.error("Échec analyse : " + (e.response?.data?.detail || e.message));
    } finally { setAnalyzing(false); }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="member-reconciliation-module">
      <header className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
        <div>
          <div className="flex items-center gap-2 text-amber-400">
            <ArrowUUpLeft size={22} weight="fill" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Réconciliation membres</span>
          </div>
          <h1 className="text-3xl font-bold text-zinc-100 mt-1">Repartir sur de bonnes bases</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Une personne à la fois. Tu reconnais ce qui s'est mal passé, tu montres ce qui a changé, tu fais une offre simple. Pas de pression.
          </p>
        </div>
        <button
          onClick={runAnalyze}
          disabled={analyzing}
          data-testid="btn-analyze"
          className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 text-xs font-bold uppercase tracking-widest transition flex items-center gap-2"
        >
          {analyzing ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
          {analyzing ? "Analyse…" : "Analyser les anciens"}
        </button>
      </header>

      {stats && (
        <div className="p-4 rounded-lg bg-zinc-900/60 border border-zinc-800">
          <p className="text-sm text-amber-300 font-bold mb-3 flex items-center gap-2">
            <ChartLine size={16} /> Cette semaine
          </p>
          <p className="text-zinc-300 text-sm mb-3">{stats.tagline}</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
            <KPI label="Total" value={stats.total_candidates} color="text-zinc-100" />
            <KPI label="À appeler" value={stats.a_contacter} color="text-red-400" />
            <KPI label="Contactés" value={stats.contacte} color="text-amber-400" />
            <KPI label="Revenus" value={stats.revenu} color="text-emerald-400" />
            <KPI label="À relancer" value={stats.a_relancer} color="text-sky-400" />
            <KPI label="Refus" value={stats.refus} color="text-zinc-500" />
          </div>
        </div>
      )}

      <nav className="flex gap-2 border-b border-zinc-800 overflow-x-auto">
        {["a_contacter", "contacte", "a_relancer", "revenu", "refus"].map(s => (
          <button
            key={s}
            onClick={() => setFilter({ status: s })}
            data-testid={`tab-${s}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition whitespace-nowrap ${
              filter.status === s ? "text-amber-300 border-amber-500" : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}
          >
            {STATUS_BADGE[s].label}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="p-12 text-center text-zinc-500">Chargement…</div>
      ) : candidates.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-zinc-700 rounded-lg">
          <Users size={32} className="text-zinc-500 mx-auto" />
          <p className="text-zinc-300 mt-2 font-bold">Aucun candidat dans cette catégorie</p>
          <p className="text-zinc-500 text-sm mt-1">Clique "Analyser les anciens" pour scanner la DB.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {candidates.map(c => <CandidateCard key={c.id} c={c} onOpen={() => setOpen(c)} />)}
        </div>
      )}

      {open && (
        <CandidateDrawer
          candidate={open}
          onClose={() => setOpen(null)}
          onUpdated={() => { loadStats(); loadCandidates(); }}
        />
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <div className="p-2 rounded bg-zinc-950 border border-zinc-800">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}

function CandidateCard({ c, onOpen }) {
  const seg = SEGMENT_BADGE[c.segment] || SEGMENT_BADGE.essai_court;
  const status = STATUS_BADGE[c.contact_status] || STATUS_BADGE.a_contacter;
  return (
    <button
      onClick={onOpen}
      data-testid={`candidate-${c.id}`}
      className="w-full text-left p-4 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-amber-700/50 transition"
    >
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-base font-bold text-zinc-100">{c.name}</span>
            <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${seg.color}`}>{seg.label}</span>
            <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${status.color}`}>{status.label}</span>
          </div>
          <div className="text-xs text-zinc-400 flex items-center gap-3 flex-wrap">
            <span>💰 {c.total_paid?.toFixed(0)} $ · {c.payment_count} paiements</span>
            <span>⏱ {c.months_active} mois actif · parti il y a {c.months_since_left} mois</span>
            {c.had_double_charge && <span className="text-red-400">⚠️ double facturation</span>}
          </div>
          <div className="text-xs text-amber-400 mt-1">
            {RAISON_LABEL[c.primary_raison] || c.primary_raison}
          </div>
          <div className="text-xs text-emerald-300 mt-1">
            💡 Offre suggérée : {c.suggested_offer?.label || "—"}
          </div>
        </div>
        <div className="text-zinc-500 text-xs">→</div>
      </div>
    </button>
  );
}

function CandidateDrawer({ candidate, onClose, onUpdated }) {
  const [tab, setTab] = useState("appel");
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logging, setLogging] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [callingCamille, setCallingCamille] = useState(false);
  const [summary, setSummary] = useState("");
  const [editableSms, setEditableSms] = useState("");
  const [editableCallScript, setEditableCallScript] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/member-reconciliation/candidates/${candidate.id}/script?channel=${tab}`)
      .then(r => {
        setScript(r.data.script);
        if (tab === "sms") setEditableSms(r.data.script || "");
        if (tab === "appel") setEditableCallScript(r.data.script || "");
      })
      .catch(() => toast.error("Erreur génération script"))
      .finally(() => setLoading(false));
  }, [tab, candidate.id]);

  async function callViaCamille() {
    if (!candidate.phone) {
      toast.error("Pas de numéro de téléphone");
      return;
    }
    const body = (editableCallScript || script || "").trim();
    if (!body) { toast.error("Script vide"); return; }
    if (!window.confirm(
      `📞 Camille (voix Polly Chantal-Neural FR-CA) va appeler ${candidate.name} au ${candidate.phone}.\n\n` +
      `Le script "élection" sera joué (${body.length} caractères).\n` +
      `Si pas de réponse → message vocal.\n` +
      `Coût estimé : ~0.15$ CAD pour 60 secondes.\n\n` +
      `Confirmer l'appel ?`
    )) return;
    setCallingCamille(true);
    try {
      const r = await api.post(`/member-reconciliation/candidates/${candidate.id}/outbound-call`, {
        script: body, voice: "Polly.Chantal-Neural", leave_voicemail: true, log_as_contact: true,
      });
      toast.success(`✅ Camille appelle ${candidate.name} · ${r.data.status} · SID ${r.data.sid?.slice(0, 12)}…`);
      onUpdated();
      const wantOutcome = window.prompt(
        "Résultat à enregistrer ? (revenu / refus / a_relancer / indecis / messagerie)\nLaisse vide pour reporter.",
        "messagerie"
      );
      if (wantOutcome && ["revenu","refus","a_relancer","indecis","messagerie"].includes(wantOutcome.trim())) {
        await api.post(`/member-reconciliation/contacts/${r.data.contact_id}/outcome`, {
          outcome: wantOutcome.trim(),
          offer_used: candidate.suggested_offer?.coupon || null,
        });
        toast.success("✅ Résultat enregistré");
        onUpdated();
      }
      onClose();
    } catch (e) {
      toast.error("Échec appel : " + (e.response?.data?.detail || e.message));
    } finally { setCallingCamille(false); }
  }

  async function sendSmsTwilio() {
    if (!candidate.phone) {
      toast.error("Pas de numéro de téléphone pour ce candidat");
      return;
    }
    const body = (editableSms || script || "").trim();
    if (!body) { toast.error("SMS vide"); return; }
    if (body.length > 1600) {
      toast.error(`SMS trop long (${body.length}/1600)`);
      return;
    }
    if (!window.confirm(`Envoyer ce SMS à ${candidate.name} (${candidate.phone}) via Twilio ?\n\nLongueur: ${body.length} caractères\nCoût estimé: ~$0.05 CAD`)) return;
    setSendingSms(true);
    try {
      const r = await api.post(`/member-reconciliation/candidates/${candidate.id}/send-sms`, {
        body, log_as_contact: true,
      });
      toast.success(`✅ SMS envoyé · SID ${r.data.sid?.slice(0, 12)}…`);
      onUpdated();
      const wantOutcome = window.prompt("Résultat à enregistrer ? (revenu / refus / a_relancer / indecis)\nLaisse vide pour reporter.", "");
      if (wantOutcome && ["revenu","refus","a_relancer","indecis"].includes(wantOutcome.trim())) {
        await api.post(`/member-reconciliation/contacts/${r.data.contact_id}/outcome`, {
          outcome: wantOutcome.trim(),
          offer_used: candidate.suggested_offer?.coupon || null,
        });
        toast.success("✅ Résultat enregistré");
        onUpdated();
      }
      onClose();
    } catch (e) {
      toast.error("Échec SMS : " + (e.response?.data?.detail || e.message));
    } finally { setSendingSms(false); }
  }

  async function logContact() {
    setLogging(true);
    try {
      const r = await api.post("/member-reconciliation/contacts", {
        candidate_id: candidate.id, channel: tab, summary,
      });
      toast.success("✅ Contact enregistré");
      const wantOutcome = window.prompt("Résultat ? (revenu / refus / a_relancer / indecis)\nLaisse vide pour reporter.", "");
      if (wantOutcome && ["revenu","refus","a_relancer","indecis"].includes(wantOutcome.trim())) {
        await api.post(`/member-reconciliation/contacts/${r.data.id}/outcome`, {
          outcome: wantOutcome.trim(),
          offer_used: candidate.suggested_offer?.coupon || null,
        });
        toast.success("✅ Résultat enregistré");
      }
      onUpdated();
      onClose();
    } catch (e) {
      toast.error("Échec : " + (e.response?.data?.detail || e.message));
    } finally { setLogging(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-lg p-6" data-testid="candidate-drawer">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-zinc-100">{candidate.name}</h2>
            <div className="text-xs text-zinc-400 mt-1">
              {candidate.email || "—"} · {candidate.phone || "—"}
            </div>
            <div className="text-xs text-amber-400 mt-1">{RAISON_LABEL[candidate.primary_raison]}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded"><X size={20} /></button>
        </div>

        <div className="p-3 rounded bg-emerald-950/30 border border-emerald-800/50 text-sm mb-4">
          <div className="text-emerald-300 font-bold text-xs uppercase tracking-widest mb-1">💡 Offre suggérée</div>
          <div className="text-zinc-100">{candidate.suggested_offer?.label}</div>
          {candidate.suggested_offer?.coupon && (
            <div className="text-xs text-emerald-400 mt-1">Coupon : <code className="bg-zinc-950 px-1.5 py-0.5 rounded">{candidate.suggested_offer.coupon}</code></div>
          )}
        </div>

        <nav className="flex gap-1 mb-3">
          {[
            { k: "appel", label: "📞 Appel" },
            { k: "sms", label: "💬 SMS" },
            { k: "email", label: "✉️ Email" },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`channel-${t.k}`}
              className={`flex-1 px-3 py-2 rounded text-xs font-bold uppercase tracking-widest ${tab === t.k ? "bg-amber-500 text-zinc-950" : "bg-zinc-900 text-zinc-400 hover:text-zinc-100"}`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="p-4 rounded bg-zinc-900 border border-zinc-800 text-sm text-zinc-200 whitespace-pre-wrap font-sans min-h-[160px] mb-4">
          {loading ? "Génération via GPT-5.2…" : (script || "Aucun script encore")}
        </div>

        {tab === "appel" && !loading && script && (
          <div className="mb-4 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-400 uppercase tracking-widest font-bold">📞 Camille (IA téléphone) prêt à appeler</span>
              <span className="text-zinc-500">{(editableCallScript || "").length} car.</span>
            </div>
            <textarea
              value={editableCallScript}
              onChange={e => setEditableCallScript(e.target.value)}
              className="w-full bg-zinc-900 border border-red-700/40 rounded p-3 text-sm text-zinc-100 font-sans"
              rows={5}
              data-testid="call-script-editable"
              placeholder="Script que Camille va jouer en voix Polly Chantal-Neural FR-CA…"
            />
            <button
              onClick={callViaCamille}
              disabled={callingCamille || !candidate.phone}
              data-testid="btn-camille-outbound-call"
              className="w-full px-4 py-3 rounded bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white text-sm font-bold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {callingCamille ? (
                <><ArrowsClockwise size={16} className="animate-spin" /> Camille compose le numéro…</>
              ) : !candidate.phone ? (
                <>📵 Pas de téléphone disponible</>
              ) : (
                <>📞 Camille appelle {candidate.name?.split(" ")[0]} maintenant</>
              )}
            </button>
            <p className="text-[10px] text-zinc-500 text-center">
              Voix Polly Chantal-Neural FR-CA · Si pas réponse → message vocal · ~0.15$ CAD/min
            </p>
          </div>
        )}

        {tab === "sms" && !loading && script && (
          <div className="mb-4 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-400 uppercase tracking-widest font-bold">📱 SMS prêt à envoyer</span>
              <span className={`${(editableSms || "").length > 160 ? "text-amber-400" : "text-emerald-400"} font-mono`}>
                {(editableSms || "").length} / 1600 car.
              </span>
            </div>
            <textarea
              value={editableSms}
              onChange={e => setEditableSms(e.target.value)}
              className="w-full bg-zinc-900 border border-amber-700/40 rounded p-3 text-sm text-zinc-100 font-sans"
              rows={6}
              data-testid="sms-editable"
            />
            <button
              onClick={sendSmsTwilio}
              disabled={sendingSms || !candidate.phone}
              data-testid="btn-send-sms-twilio"
              className="w-full px-4 py-3 rounded bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-zinc-950 text-sm font-bold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sendingSms ? (
                <><ArrowsClockwise size={16} className="animate-spin" /> Envoi Twilio…</>
              ) : !candidate.phone ? (
                <>📵 Pas de téléphone disponible</>
              ) : (
                <>📲 Envoyer SMS Twilio à {candidate.name?.split(" ")[0]}</>
              )}
            </button>
            <p className="text-[10px] text-zinc-500 text-center">
              Coût ~0.05$ CAD · Logge automatiquement le contact + propose résultat
            </p>
          </div>
        )}

        <textarea
          value={summary}
          onChange={e => setSummary(e.target.value)}
          placeholder="Résumé du contact (optionnel)"
          className="w-full bg-zinc-900 border border-zinc-800 rounded p-3 text-sm text-zinc-200 mb-3"
          rows={3}
          data-testid="contact-summary"
        />

        <button
          onClick={logContact}
          disabled={logging}
          data-testid="btn-log-contact"
          className="w-full px-4 py-3 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-sm font-bold uppercase tracking-widest disabled:opacity-50"
        >
          {logging ? "Enregistre…" : `✅ J'ai fait le ${tab}`}
        </button>

        <button
          onClick={async () => {
            const reason = window.prompt(
              `🚫 Pourquoi enlever ${candidate.name} de la liste ?\n\n` +
              "Raisons typiques :\n" +
              "- arrangement_kru (entente services privés)\n" +
              "- vip_private (cliente privée seulement)\n" +
              "- deja_revenu (déjà actif)\n" +
              "- ne_veut_pas_revenir\n" +
              "- decede\n" +
              "- autre",
              "arrangement_kru"
            );
            if (!reason) return;
            try {
              await api.post(`/member-reconciliation/candidates/${candidate.id}/blacklist?reason=${encodeURIComponent(reason)}`);
              toast.success(`🚫 ${candidate.name} enlevé de la liste (${reason})`);
              onUpdated();
              onClose();
            } catch (e) {
              toast.error("Échec : " + (e.response?.data?.detail || e.message));
            }
          }}
          data-testid="btn-blacklist"
          className="mt-2 w-full px-4 py-2 rounded bg-zinc-900 hover:bg-red-900/30 border border-red-700/40 text-red-400 text-xs font-bold uppercase tracking-widest transition"
        >
          🚫 Ne plus contacter — enlever de la liste
        </button>

        {candidate.contacts_history?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Historique</div>
            {candidate.contacts_history.map(h => (
              <div key={h.id} className="text-xs text-zinc-400 p-2 border-l-2 border-amber-500/40 pl-3 mb-2">
                <div>{new Date(h.contacted_at).toLocaleString("fr-CA")} · {h.channel}{h.outcome ? ` → ${h.outcome}` : ""}</div>
                {h.summary && <div className="text-zinc-500 mt-1">{h.summary}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
